import { Device } from "@capacitor/device";
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { ALL_FILE_TYPES } from "../constants/common.constant";
import { FilePicker } from "@capawesome/capacitor-file-picker";
import { Filesystem } from "@capacitor/filesystem";
import toasty from "./toasty.util";

const getPlatformName = async () => {
    if (window.electronAPI) return 'desktop'
    const info = await Device.getInfo();
    return info.platform
}

function getDeviceName() {
    const userAgent = navigator.userAgent;

    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
        return "IOS";
    } else if (/Android/.test(userAgent)) {
        return "ANDROID";
    } else if (/Macintosh/.test(userAgent)) {
        return "MAC";
    } else if (/Windows/.test(userAgent)) {
        return "WINDOWS";
    } else if (/Linux/.test(userAgent)) {
        return "LINUX";
    } else {
        return "Unknown";
    }
}

const isDesktopPlatform = async () => {
    const platform = await getPlatformName()
    const device = getDeviceName()

    if (platform === 'desktop') return true
    else if (platform === 'web' && ['WINDOWS', 'LINUX', 'MAC'].includes(device)) return true
    else return false
}

const isNativePlatform = async () => {
    const platform = await getPlatformName()
    return (platform === 'android' || platform === 'ios')
}

const isElectronApp = async () => {
    const platform = await getPlatformName()
    return (platform === 'desktop')
}

const isWebPlatform = async () => {
    const platform = await getPlatformName()
    const device = getDeviceName()
    if (platform === 'web' && ['WINDOWS', 'LINUX', 'MAC'].includes(device)) return true
    else return false
}

const reload = () => {
    if (window.electronAPI) window.electronAPI.reloadMainWindow()
    else window.location.reload()
}

const navigateTo = (value) => {
    window.location.href = value
    if (window.electronAPI) window.electronAPI.navigateTo(value)
}

const focus = () => {
    if (window.electronAPI) window.electronAPI.focusMainWindow()
    else window.focus()
}

const canAccessScreen = async () => {
    if (window.electronAPI) return window.electronAPI.canAccessScreen()
    return true
}

const checkNativeFilePermission = async () => {
    try {
        const platform = Capacitor.getPlatform();

        // ✅ iOS doesn't need explicit permission
        if (platform === "ios") return true;

        // 🔹 Check existing permission first
        const current = await FilePicker.checkPermissions();

        if (current?.readExternalStorage === "granted") {
            return true;
        }

        // 🔹 Request permission if not granted
        const permissionResult = await FilePicker.requestPermissions();

        return permissionResult?.readExternalStorage === "granted";

    } catch (error) {
        console.error("Permission check error:", error);
        return false;
    }
};

const openLinkOnNewTab = (href) => {
    if (window.electronAPI) {
        window.electronAPI.openExternalLink(href)
    } else {
        window.open(href, '_blank')
    }
}

const createNewTab = async (href, toasty) => {
    if (window.electronAPI) {
        const length = await window.electronAPI.getTabsLength()
        if (length >= 15) return toasty.error("Tab limit reached")
        window.electronAPI.openNewTab(href)
    } else {
        window.open(href, '_blank', 'noopener noreferrer')
    }
}

const vibrateDevice = async (intensity = "Light") => {
    if (await isNativePlatform()) {
        await Haptics.impact({ style: ImpactStyle[intensity] });
    }
}

const print = (html) => {
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    document.body.appendChild(iframe);

    const doc = iframe.contentWindow.document;
    doc.open();
    doc.writeln(html);
    doc.close();

    iframe.contentWindow.focus();
    iframe.contentWindow.print();

    setTimeout(() => document.body.removeChild(iframe), 1000);
}

const isValidFile = (file, allowedExtensions) => {
    if (!allowedExtensions?.length) return true;

    const ext = file.name.split(".").pop().toLowerCase();
    return allowedExtensions.includes(ext);
};

const nativeFilePicker = async ({ types = ALL_FILE_TYPES, multiple = false, allowedExtensions = [], errorMessage = "Invalid file type selected" } = {}) => {
        try {
        const result = await FilePicker.pickFiles({ types, multiple });

        if (!result?.files?.length) return null;

        const files = await Promise.all(
            result.files
                .filter(file => file?.path && file?.name)
                .map(async (file) => {
                    try {
                        const fileData = await Filesystem.readFile({
                            path: file.path,
                        });

                        let mime = file.mimeType || "application/octet-stream";

                        if (mime === "text/comma-separated-values") {
                            mime = "text/csv";
                        }

                        const cleaned = fileData.data.replace(/^data:.*;base64,/, '');

                        const byteCharacters = atob(cleaned);
                        const byteNumbers = new Array(byteCharacters.length)
                            .fill(null)
                            .map((_, i) => byteCharacters.charCodeAt(i));

                        const blob = new Blob(
                            [new Uint8Array(byteNumbers)],
                            { type: mime }
                        );

                        // ✅ Multer-compatible File
                        return new File([blob], file.name, { type: mime });

                    } catch (err) {
                        console.error("Blob conversion failed:", err);
                        return null;
                    }
                })
        );

        //* Validate by extension
        const validFiles = files
            .filter(Boolean)
            .filter(file => {
                if (!allowedExtensions?.length) return true;

                const ext = file.name.split(".").pop().toLowerCase();
                return allowedExtensions.includes(ext);
            });


        //🔥 If nothing valid
        if (!validFiles.length) {
            toasty.error(errorMessage);
            return null;
        }

        //* Return based on multiple flag
        return multiple ? validFiles : validFiles[0];

    } catch (error) {
        //👇 IMPORTANT: user cancel comes here too
        if (error?.message?.includes("User cancelled")) {
            return null;
        }

        console.error("File picker error:", error);
        return null;
    }

};

export {
    getPlatformName,
    reload,
    navigateTo,
    focus,
    canAccessScreen,
    getDeviceName,
    isDesktopPlatform,
    isNativePlatform,
    isElectronApp,
    isWebPlatform,
    checkNativeFilePermission,
    openLinkOnNewTab,
    createNewTab,
    vibrateDevice,
    print,
    nativeFilePicker,
    isValidFile
}